// src/components/DAOStatus.js
import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import governorAbi from "../abi/SimpleGovernor.json";
import catCoinAbi from "../abi/CatCoin.json";
import treasuryAbi from "../abi/Treasury.json";
import charityAbi from "../abi/CharityWallet.json";
import contestAbi from "../abi/CatContest.json";

const DAOStatus = () => {
  const [daoInfo, setDaoInfo] = useState({
    votingDelay: "-",
    votingPeriod: "-",
    quorum: "-",
    proposalDeposit: "-",
    totalSupply: "-",
    maxSupply: "-",
    treasuryCAT: "-",
    treasuryETH: "-",
    charityCAT: "-",
    charityETH: "-",
    claimAvailable: "-",
    waitTime: "-",
    claimAmount: "-",
    // 🔧 new fields
    mintInterval: "-",
    mintAmount: "-",
    contestRewardPct: "-",
    contestCharityPct: "-",
    contestVotingPeriod: "-",
    contestWinners: "-",
    contestMaxVotes: "-",
  });

  const [loading, setLoading] = useState(false);
  const [account, setAccount] = useState("");

  useEffect(() => {
    if (window.ethereum) {
      window.ethereum.request({ method: "eth_accounts" }).then((accs) => {
        if (accs && accs.length > 0) setAccount(accs[0]);
      });
    }
  }, []);

  // .env
  const governorAddr = process.env.REACT_APP_GOVERNOR_CONTRACT;
  const tokenAddr = process.env.REACT_APP_TOKEN_CONTRACT;
  const treasuryAddr = process.env.REACT_APP_TREASURY_CONTRACT;
  const charityAddr = process.env.REACT_APP_CHARITY_CONTRACT;
  const contestAddr = process.env.REACT_APP_CONTEST_CONTRACT;

  const fmt = (x, d = 18) => parseFloat(ethers.formatUnits(x, d));

  const loadDAOStatus = async () => {
    try {
      if (!window.ethereum) {
        alert("⚠️ Please install MetaMask!");
        return;
      }
      setLoading(true);
      const provider = new ethers.BrowserProvider(window.ethereum);
      const governor = new ethers.Contract(governorAddr, governorAbi, provider);
      const token = new ethers.Contract(tokenAddr, catCoinAbi, provider);
      const contest = new ethers.Contract(contestAddr, contestAbi, provider);
      const treasury = new ethers.Contract(treasuryAddr, treasuryAbi, provider);
      const charity = new ethers.Contract(charityAddr, charityAbi, provider);

      // ============ 1) Governance parameters ============
      let votingDelayStr = "-", votingPeriodStr = "-", proposalDepositStr = "-", quorumStr = "-";
      try {
        const votingDelay = await governor.votingDelay();
        const votingPeriod = await governor.votingPeriod();
        votingDelayStr = `${(Number(votingDelay) / 60).toFixed(1)} minutes`;
        votingPeriodStr = `${(Number(votingPeriod) / 60).toFixed(1)} minutes`;
      } catch {}

      try {
        const proposalDeposit = await governor.proposalDeposit();
        proposalDepositStr = `${ethers.formatUnits(proposalDeposit, 18)} CAT`;
      } catch {}

      try {
        const quorumPct = await governor.quorumPercentage();
        quorumStr = `${Number(quorumPct).toFixed(2)} %`;
      } catch {}

      // ============ 2) Token supply ============
      let totalSupplyStr = "-",
          maxSupplyStr = "-",
          mintIntervalStr = "-",
          mintAmountStr = "-";

      try {
        const totalSupply = await token.totalSupply();
        totalSupplyStr = fmt(totalSupply, 18).toLocaleString();
      } catch {}

      try {
        const maxSupply = await token.MAX_SUPPLY();
        maxSupplyStr = fmt(maxSupply, 18).toLocaleString();
      } catch {}

      try {
        const mintInterval = await token.mintInterval();
        const mintAmount = await token.mintAmount();
        mintIntervalStr = `${Number(mintInterval) / 60} minutes`;
        mintAmountStr = `${fmt(mintAmount, 18).toFixed(2)} CAT`;
      } catch {}

      // ============ 3) Treasury & Charity ============
      let tETHstr = "-", tCATstr = "-", cETHstr = "-", cCATstr = "-";
      try {
        const tb = await treasury.getBalances();
        tETHstr = parseFloat(ethers.formatEther(tb[0])).toFixed(4);
        tCATstr = fmt(tb[1]).toFixed(2);
      } catch {}

      try {
        const cb = await charity.getBalances();
        cETHstr = parseFloat(ethers.formatEther(cb[0])).toFixed(4);
        cCATstr = fmt(cb[1]).toFixed(2);
      } catch {}

      // ============ 4) Claim Info ============
      let claimAvailable = "-", waitTime = "-", claimAmount = "-";
      if (account) {
        try {
          const res = await token.canClaimInitialCAT(account);
          claimAvailable = res[0] ? "✅ Yes" : "❌ No";
          waitTime = res[1] > 0 ? `${(Number(res[1]) / 3600).toFixed(1)} hrs` : "-";
          claimAmount = fmt(res[2]).toFixed(2);
        } catch {}
      }

      // ============ 5) Contest parameters ============
      let rewardPctStr = "-", charityPctStr = "-", votingPeriodStr2 = "-", winnersStr = "-", maxVotesStr = "-";
      try {
        const rewardPct = await contest.rewardPercentage();
        const charityPct = await contest.charityPercentage();
        const votingPeriod = await contest.votingPeriod();
        const winners = await contest.winnersCount();
        const maxVotes = await contest.maxVotesPerUser();

        rewardPctStr = `${Number(rewardPct)}%`;
        charityPctStr = `${Number(charityPct)}%`;
        votingPeriodStr2 = `${(Number(votingPeriod) / 60).toFixed(1)} minutes`;
        winnersStr = `${Number(winners)}`;
        maxVotesStr = `${fmt(maxVotes)} CAT`;
      } catch (err) {
        console.warn("⚠️ Contest param read failed:", err);
      }

      setDaoInfo({
        votingDelay: votingDelayStr,
        votingPeriod: votingPeriodStr,
        quorum: quorumStr,
        proposalDeposit: proposalDepositStr,
        totalSupply: totalSupplyStr,
        maxSupply: maxSupplyStr,
        treasuryCAT: tCATstr,
        treasuryETH: tETHstr,
        charityCAT: cCATstr,
        charityETH: cETHstr,
        claimAvailable,
        waitTime,
        claimAmount,
        // new
        mintInterval: mintIntervalStr,
        mintAmount: mintAmountStr,
        contestRewardPct: rewardPctStr,
        contestCharityPct: charityPctStr,
        contestVotingPeriod: votingPeriodStr2,
        contestWinners: winnersStr,
        contestMaxVotes: maxVotesStr,
      });

      setLoading(false);
    } catch (err) {
      console.error("❌ loadDAOStatus failed:", err);
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDAOStatus();
  }, [account]);

  return (
    <div className="card" style={cardStyle}>
      {loading ? (
        <p>Loading data from blockchain...</p>
      ) : (
        <>
          {/* Governance */}
          <h3 style={sectionHeader}>📊 Governance Parameters</h3>
          <table style={tableStyle}>
            <tbody>
              <tr><td style={tdLeft}>🕒 Voting Delay</td><td style={tdRight}>{daoInfo.votingDelay}</td></tr>
              <tr><td style={tdLeft}>📅 Voting Period</td><td style={tdRight}>{daoInfo.votingPeriod}</td></tr>
              <tr><td style={tdLeft}>⚖️ Quorum</td><td style={tdRight}>{daoInfo.quorum}</td></tr>
              <tr><td style={tdLeft}>💰 Proposal Deposit</td><td style={tdRight}>{daoInfo.proposalDeposit}</td></tr>
            </tbody>
          </table>

          {/* Token */}
          <h3 style={sectionHeader}>🪙 CatCoin Token</h3>
          <table style={tableStyle}>
            <tbody>
              <tr><td style={tdLeft}>Total Supply</td><td style={tdRight}>{daoInfo.totalSupply} CAT</td></tr>
              <tr><td style={tdLeft}>Max Supply</td><td style={tdRight}>{daoInfo.maxSupply} CAT</td></tr>
              <tr><td style={tdLeft}>Claim Available</td><td style={tdRight}>{daoInfo.claimAvailable}</td></tr>
              <tr><td style={tdLeft}>Next Claim Wait</td><td style={tdRight}>{daoInfo.waitTime}</td></tr>
              <tr><td style={tdLeft}>Claim Amount</td><td style={tdRight}>{daoInfo.claimAmount} CAT</td></tr>
            </tbody>
          </table>

          {/* Treasury */}
          <h3 style={sectionHeader}>🏦 Treasury Wallet</h3>
          <table style={tableStyle}>
            <tbody>
              <tr><td style={tdLeft}>ETH Balance</td><td style={tdRight}>{daoInfo.treasuryETH} ETH</td></tr>
              <tr><td style={tdLeft}>CAT Balance</td><td style={tdRight}>{daoInfo.treasuryCAT} CAT</td></tr>
            </tbody>
          </table>

          {/* Charity */}
          <h3 style={sectionHeader}>💖 Charity Wallet</h3>
          <table style={tableStyle}>
            <tbody>
              <tr><td style={tdLeft}>ETH Balance</td><td style={tdRight}>{daoInfo.charityETH} ETH</td></tr>
              <tr><td style={tdLeft}>CAT Balance</td><td style={tdRight}>{daoInfo.charityCAT} CAT</td></tr>
            </tbody>
          </table>

          {/* Governable Parameters */}
          <h3 style={sectionHeader}>⚙️ Governable Parameters</h3>
          <table style={tableStyle}>
            <tbody>
              <tr><td colSpan="2"><b>CatCoin Parameters</b></td></tr>
              <tr><td style={tdLeft}>mintInterval</td><td style={tdRight}>{daoInfo.mintInterval}</td></tr>
              <tr><td style={tdLeft}>mintAmount</td><td style={tdRight}>{daoInfo.mintAmount}</td></tr>
              <tr><td colSpan="2"><b>Cat Contest Parameters</b></td></tr>
              <tr><td style={tdLeft}>winnersCount</td><td style={tdRight}>{daoInfo.contestWinners}</td></tr>
              <tr><td style={tdLeft}>rewardPct</td><td style={tdRight}>{daoInfo.contestRewardPct}</td></tr>
              <tr><td style={tdLeft}>charityPct</td><td style={tdRight}>{daoInfo.contestCharityPct}</td></tr>
              <tr><td style={tdLeft}>votingPeriod</td><td style={tdRight}>{daoInfo.contestVotingPeriod}</td></tr>
              <tr><td style={tdLeft}>maxVotes</td><td style={tdRight}>{daoInfo.contestMaxVotes}</td></tr>
            </tbody>
          </table>

          <button onClick={loadDAOStatus} style={btnStyle}>🔄 Refresh Data</button>
        </>
      )}
    </div>
  );
};

// styles
const cardStyle = { padding: "1.8rem", borderRadius: "12px", backgroundColor: "#fafafa", boxShadow: "0px 2px 10px rgba(0,0,0,0.1)", width: "95%", maxWidth: "800px", margin: "1rem auto" };
const tableStyle = { width: "100%", borderCollapse: "collapse", fontSize: "1rem", lineHeight: "1.7", marginBottom: "1.5rem" };
const tdLeft = { padding: "0.4rem 0.8rem", textAlign: "left", color: "#444", width: "50%", whiteSpace: "nowrap" };
const tdRight = { padding: "0.4rem 0.8rem", textAlign: "right", color: "#222", fontWeight: "500", width: "50%", whiteSpace: "nowrap", fontFamily: "monospace" };
const btnStyle = { padding: "0.6rem 1.2rem", backgroundColor: "#ffcc33", border: "none", borderRadius: "8px", cursor: "pointer", fontWeight: "bold", marginTop: "1rem" };
const sectionHeader = { borderBottom: "2px solid #ffcc33", paddingBottom: "0.3rem", marginTop: "1.2rem" };

export default DAOStatus;
